import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { BanknotesIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { Button, Card, CardHeader, Input } from '@/components/common';
import { brokerService, type POSPBankDetailsUpdate } from '@/services/brokers';
// Imported by path: `@/services/index.ts` re-exports getErrorMessage but not
// getFieldErrors, and that file is outside this task's domain.
import { getErrorMessage, getFieldErrors } from '@/services/api';

/**
 * POSP-1 — the agent's bank account, on the agent's own portal.
 *
 * Two facts shape this component, and both are server-side:
 *
 * 1. The account number is NEVER on the profile payload. `posp` carries
 *    `bank_account_number_masked` and nothing more, for everybody including
 *    the agent. The full number is fetched only when the agent presses Edit,
 *    from `/brokers/posps/{id}/bank-details/`, which 403s for a Broker Admin, a
 *    super-POSP reading a sub-POSP, a country admin and a super_admin alike.
 *    So this component cannot leak a number it was never given.
 *
 * 2. `''` and `undefined` are different answers. `''` means no account is on
 *    file; `undefined` means this viewer is not shown bank details at all.
 *    Rendering either as "••••" would be a claim the data does not support.
 *
 * The edit fetch renders loading / error / loaded as three states. A failed
 * fetch that painted an empty form would invite the agent to retype their
 * account number over a value nobody actually read — the platform's signature
 * bug, on the one field where it costs money.
 */

const EMPTY_FORM: Required<POSPBankDetailsUpdate> = {
  bank_name: '',
  bank_account_number: '',
  bank_ifsc_code: '',
};

export function PospBankDetailsPanel({
  pospId,
  bankName,
  ifscCode,
  maskedAccountNumber,
  canEdit,
}: {
  pospId: string;
  bankName: string | undefined;
  ifscCode: string | undefined;
  /** Last four digits behind bullets. `''` = nothing on file; `undefined` =
   *  not disclosed to this viewer. */
  maskedAccountNumber: string | undefined;
  /** Only the agent themselves. A super-POSP looking at a team member sees the
   *  same masked block with no Edit button — and would be refused server-side
   *  even if one were drawn. */
  canEdit: boolean;
}) {
  const queryClient = useQueryClient();
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState<Required<POSPBankDetailsUpdate>>(EMPTY_FORM);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitError, setSubmitError] = useState<string | null>(null);

  // The explicit EDIT fetch. Nothing requests it until the agent asks to edit.
  const {
    data: full,
    isLoading: isLoadingFull,
    isError: fullFailed,
    error: fullError,
    refetch: refetchFull,
  } = useQuery({
    queryKey: ['pospBankDetails', pospId],
    queryFn: () => brokerService.getPOSPBankDetails(pospId),
    enabled: isEditing && canEdit,
    // Never cached beyond the edit: this is the one payload carrying the whole
    // account number, and it should not sit in memory once the form closes.
    gcTime: 0,
    staleTime: 0,
    retry: false,
  });

  useEffect(() => {
    if (!full) return;
    setForm({
      bank_name: full.bank_name ?? '',
      bank_account_number: full.bank_account_number ?? '',
      bank_ifsc_code: full.bank_ifsc_code ?? '',
    });
  }, [full]);

  const save = useMutation({
    mutationFn: (payload: POSPBankDetailsUpdate) => brokerService.updatePOSPBankDetails(pospId, payload),
    onSuccess: (stored) => {
      // Repaint every screen that shows the mask: the agent's own profile and
      // the Broker Admin's channel detail page both read it from elsewhere.
      queryClient.invalidateQueries({ queryKey: ['posp'] });
      queryClient.invalidateQueries({ queryKey: ['unifiedChannel'] });
      queryClient.invalidateQueries({ queryKey: ['unifiedChannels'] });
      queryClient.removeQueries({ queryKey: ['pospBankDetails', pospId] });
      toast.success(
        stored.bank_account_number
          ? `Bank details saved — account ending ${stored.bank_account_number.slice(-4)}.`
          : 'Bank details cleared — no account is on file.'
      );
      closeEditor();
    },
    onError: (error) => {
      // Both, deliberately. The toast is what the agent notices; the inline
      // errors are what tell them WHICH field to fix, and they survive the
      // toast disappearing.
      const fields = getFieldErrors(error);
      setFieldErrors(fields);
      setSubmitError(getErrorMessage(error));
      toast.error(getErrorMessage(error));
    },
  });

  const closeEditor = () => {
    setIsEditing(false);
    setForm(EMPTY_FORM);
    setFieldErrors({});
    setSubmitError(null);
  };

  const set = (key: keyof POSPBankDetailsUpdate, value: string) => {
    setForm((f) => ({ ...f, [key]: value }));
    setFieldErrors((e) => {
      if (!(key in e)) return e;
      const next = { ...e };
      delete next[key];
      return next;
    });
  };

  const notDisclosed = maskedAccountNumber === undefined;
  const nothingOnFile = maskedAccountNumber === '';

  return (
    <Card>
      <CardHeader
        title="Bank Details"
        subtitle={
          canEdit
            ? 'Where your commission payouts are sent. Only you can see or change the full account number.'
            : 'Where this agent’s commission payouts are sent. The full account number is theirs alone.'
        }
      />

      {!isEditing ? (
        <>
          <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div>
              <dt className="text-xs text-gray-500 dark:text-gray-400">Bank</dt>
              <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100">{bankName || '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500 dark:text-gray-400">Account Number</dt>
              <dd className="mt-1 text-sm font-medium text-gray-900 dark:text-gray-100" data-testid="posp-bank-account-masked">
                {notDisclosed ? (
                  <span className="text-gray-400">Not shown</span>
                ) : nothingOnFile ? (
                  <span className="text-gray-400">No account on file</span>
                ) : (
                  <span className="font-mono tracking-wide">{maskedAccountNumber}</span>
                )}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-gray-500 dark:text-gray-400">IFSC</dt>
              <dd className="mt-1 font-mono text-sm font-medium text-gray-900 dark:text-gray-100">{ifscCode || '—'}</dd>
            </div>
          </dl>

          {canEdit && (
            <div className="mt-4">
              <Button
                variant="outline"
                size="sm"
                leftIcon={<BanknotesIcon className="h-4 w-4" />}
                onClick={() => setIsEditing(true)}
              >
                {nothingOnFile ? 'Add bank details' : 'Edit bank details'}
              </Button>
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Payouts are made to this account. Nobody at your broker can change it — only you.
              </p>
            </div>
          )}
        </>
      ) : isLoadingFull ? (
        <p className="py-4 text-sm text-gray-500 dark:text-gray-400">Loading your bank details…</p>
      ) : fullFailed ? (
        // Three states, not two. An empty form here would ask the agent to
        // retype an account number over a value nobody read back.
        <div className="flex items-start gap-2 rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
          <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 flex-shrink-0 text-red-500 dark:text-red-400" />
          <div>
            <p className="text-sm font-medium text-red-800 dark:text-red-200">
              We could not load your bank details — this is not the same as there being none.
            </p>
            {/* getErrorMessage handles any shape, including a non-Error
                rejection; guarding on `instanceof Error` here would have
                swallowed the server's own explanation and printed a generic
                line instead. */}
            <p className="mt-1 text-xs text-red-600 dark:text-red-400">{getErrorMessage(fullError)}</p>
            <div className="mt-2 flex gap-2">
              <Button size="sm" variant="outline" onClick={() => refetchFull()}>
                Try again
              </Button>
              <Button size="sm" variant="outline" onClick={closeEditor}>
                Cancel
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Input
              label="Bank Name"
              name="bank_name"
              id="bank_name"
              value={form.bank_name}
              onChange={(e) => set('bank_name', e.target.value)}
              error={fieldErrors.bank_name}
            />
            <Input
              label="Account Number"
              name="bank_account_number"
              id="bank_account_number"
              inputMode="numeric"
              value={form.bank_account_number}
              onChange={(e) => set('bank_account_number', e.target.value)}
              error={fieldErrors.bank_account_number}
              helperText="Digits only, as printed on your passbook."
            />
            <Input
              label="IFSC Code"
              name="bank_ifsc_code"
              id="bank_ifsc_code"
              value={form.bank_ifsc_code}
              onChange={(e) => set('bank_ifsc_code', e.target.value.toUpperCase())}
              error={fieldErrors.bank_ifsc_code}
              helperText="e.g. HDFC0001234"
            />
          </div>

          {submitError && !Object.keys(fieldErrors).length && (
            <p className="text-sm text-red-600 dark:text-red-400" role="alert">
              {submitError}
            </p>
          )}

          <div className="flex gap-2">
            <Button
              onClick={() => {
                setSubmitError(null);
                setFieldErrors({});
                save.mutate({
                  bank_name: form.bank_name.trim(),
                  bank_account_number: form.bank_account_number.trim(),
                  bank_ifsc_code: form.bank_ifsc_code.trim().toUpperCase(),
                });
              }}
              isLoading={save.isPending}
            >
              Save bank details
            </Button>
            <Button variant="outline" onClick={closeEditor} disabled={save.isPending}>
              Cancel
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
